import { createClient, SupabaseClient } from '@supabase/supabase-js';
import { ENV } from '../env';
import type {
  LocalMapeado,
  BemPatrimonial,
  MovimentacaoPatrimonial,
  PapelUsuario,
  UsuarioAcesso,
  SolicitacaoLaudoRegistro,
  Campus,
  Sede,
  Bloco,
  Andar,
  Sala
} from '../types';

class SupabaseService {
  private client: SupabaseClient<any, any, any>;
  private schema = 'inventariarutf';

  constructor() {
    this.client = createClient(ENV.SUPABASE_URL, ENV.SUPABASE_KEY, {
      db: { schema: this.schema },
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: true,
      },
    });
  }

  getClient() {
    return this.client;
  }

  // Helper para acessar schema específico
  private getSchema() {
    return this.client.schema(this.schema);
  }

  // Nomes de tabelas dinâmicas baseadas no campus
  getTabelaPatrimonio(campusId: string) {
    return `view_patrimonio_atual_${campusId.toLowerCase()}`;
  }

  getTabelaInventario(campusId: string) {
    return `inventario_em_andamento_${campusId.toLowerCase()}`;
  }

  getTabelaViewVerificacao(baseView: string, campusId: string) {
    return `${baseView}_${campusId}`;
  }

  // Operações de Locais (legado - usa view de compatibilidade)
  async buscarLocaisPorCampus(campusId: string): Promise<LocalMapeado[]> {
    // Buscar das novas tabelas hierárquicas
    const { data: salasData, error: salasError } = await this.getSchema()
      .from('locais_salas')
      .select(`
        id,
        nome,
        descricao,
        setor,
        andar_id,
        locais_andares (
          id,
          nome,
          descricao,
          bloco_id,
          locais_blocos (
            id,
            nome,
            sigla,
            descricao,
            sede_id,
            locais_sedes (
              id,
              nome,
              sigla,
              campus_sigla,
              campus_sigla
            )
          )
        )
      `)
      .eq('locais_andares.locais_blocos.locais_sedes.campus_sigla', campusId.toUpperCase())
      .eq('ativo', true);

    if (salasError) throw salasError;

    const locaisMapeados: LocalMapeado[] = [];

    for (const sala of salasData || []) {
      const andar = (sala as any).locais_andares;
      const bloco = andar?.locais_blocos;
      const sede = bloco?.locais_sedes;

      if (!andar || !bloco || !sede) continue;

      locaisMapeados.push({
        campus: campusId,
        sede: sede.nome,
        bloco: bloco.nome,
        descricaoBloco: bloco.descricao || '',
        andar: andar.nome,
        sala: sala.nome,
        descricao: sala.descricao || '',
        setor: sala.setor || ''
      });
    }

    return locaisMapeados.sort((a, b) => {
      if (a.sede !== b.sede) return a.sede.localeCompare(b.sede);
      if (a.bloco !== b.bloco) return a.bloco.localeCompare(b.bloco);
      if (a.andar !== b.andar) return a.andar.localeCompare(b.andar);
      return a.sala.localeCompare(b.sala);
    });
  }

  // ============================================================================
  // Operações de Locais Hierarquizados
  // ============================================================================

  // Campus
  async buscarTodosCampus(): Promise<Campus[]> {
    const { data, error } = await this.getSchema()
      .from('locais_campus')
      .select('*')
      .order('nome');

    if (error) throw error;
    return data || [];
  }

  async criarCampus(campus: Omit<Campus, 'id'>): Promise<Campus> {
    const { data, error } = await this.getSchema()
      .from('locais_campus')
      .insert(campus)
      .select('*')
      .single();

    if (error) throw error;
    return data as Campus;
  }

  async atualizarCampus(id: string, campus: Partial<Omit<Campus, 'id'>>): Promise<void> {
    const { error } = await this.getSchema()
      .from('locais_campus')
      .update(campus)
      .eq('id', id);

    if (error) throw error;
  }

  async excluirCampus(id: string): Promise<void> {
    const { error } = await this.getSchema()
      .from('locais_campus')
      .delete()
      .eq('id', id);

    if (error) throw error;
  }

  // Sedes
  async buscarSedesPorCampus(campusSigla: string): Promise<Sede[]> {
    let query = this.getSchema()
      .from('locais_sedes')
      .select('*');
    
    if (campusSigla) {
      query = query.eq('campus_sigla', campusSigla);
    }
    
    const { data, error } = await query.order('nome');

    if (error) throw error;
    return data || [];
  }

  async criarSede(sede: Omit<Sede, 'id'>): Promise<Sede> {
    const { data, error } = await this.getSchema()
      .from('locais_sedes')
      .insert(sede)
      .select('*')
      .single();

    if (error) throw error;
    return data as Sede;
  }

  async atualizarSede(id: string, sede: Partial<Omit<Sede, 'id'>>): Promise<void> {
    const { error } = await this.getSchema()
      .from('locais_sedes')
      .update(sede)
      .eq('id', id);

    if (error) throw error;
  }

  async inativarSede(id: string): Promise<void> {
    const { error } = await this.getSchema()
      .from('locais_sedes')
      .update({ ativo: false })
      .eq('id', id);

    if (error) throw error;
  }

  async ativarSede(id: string): Promise<void> {
    const { error } = await this.getSchema()
      .from('locais_sedes')
      .update({ ativo: true })
      .eq('id', id);

    if (error) throw error;
  }

  async excluirSede(id: string): Promise<void> {
    const { error } = await this.getSchema()
      .from('locais_sedes')
      .delete()
      .eq('id', id);

    if (error) throw error;
  }

  // Blocos
  async buscarBlocosPorSede(sedeId: string): Promise<Bloco[]> {
    let query = this.getSchema()
      .from('locais_blocos')
      .select('*');
    
    if (sedeId) {
      query = query.eq('sede_id', sedeId);
    }
    
    const { data, error } = await query.order('nome');

    if (error) throw error;
    return data || [];
  }

  async criarBloco(bloco: Omit<Bloco, 'id'>): Promise<Bloco> {
    const { data, error } = await this.getSchema()
      .from('locais_blocos')
      .insert(bloco)
      .select('*')
      .single();

    if (error) throw error;
    return data as Bloco;
  }

  async atualizarBloco(id: string, bloco: Partial<Omit<Bloco, 'id'>>): Promise<void> {
    const { error } = await this.getSchema()
      .from('locais_blocos')
      .update(bloco)
      .eq('id', id);

    if (error) throw error;
  }

  async inativarBloco(id: string): Promise<void> {
    const { error } = await this.getSchema()
      .from('locais_blocos')
      .update({ ativo: false })
      .eq('id', id);

    if (error) throw error;
  }

  async ativarBloco(id: string): Promise<void> {
    const { error } = await this.getSchema()
      .from('locais_blocos')
      .update({ ativo: true })
      .eq('id', id);

    if (error) throw error;
  }

  async excluirBloco(id: string): Promise<void> {
    const { error } = await this.getSchema()
      .from('locais_blocos')
      .delete()
      .eq('id', id);

    if (error) throw error;
  }

  // Andares
  async buscarAndaresPorBloco(blocoId: string): Promise<Andar[]> {
    let query = this.getSchema()
      .from('locais_andares')
      .select('*');
    
    if (blocoId) {
      query = query.eq('bloco_id', blocoId);
    }
    
    const { data, error } = await query.order('nome');

    if (error) throw error;
    return data || [];
  }

  async criarAndar(andar: Omit<Andar, 'id'>): Promise<Andar> {
    const { data, error } = await this.getSchema()
      .from('locais_andares')
      .insert(andar)
      .select('*')
      .single();

    if (error) throw error;
    return data as Andar;
  }

  async atualizarAndar(id: string, andar: Partial<Omit<Andar, 'id'>>): Promise<void> {
    const { error } = await this.getSchema()
      .from('locais_andares')
      .update(andar)
      .eq('id', id);

    if (error) throw error;
  }

  async inativarAndar(id: string): Promise<void> {
    const { error } = await this.getSchema()
      .from('locais_andares')
      .update({ ativo: false })
      .eq('id', id);

    if (error) throw error;
  }

  async ativarAndar(id: string): Promise<void> {
    const { error } = await this.getSchema()
      .from('locais_andares')
      .update({ ativo: true })
      .eq('id', id);

    if (error) throw error;
  }

  async excluirAndar(id: string): Promise<void> {
    const { error } = await this.getSchema()
      .from('locais_andares')
      .delete()
      .eq('id', id);

    if (error) throw error;
  }

  // Salas
  async buscarSalasPorAndar(andarId: string): Promise<Sala[]> {
    let query = this.getSchema()
      .from('locais_salas')
      .select('*');
    
    if (andarId) {
      query = query.eq('andar_id', andarId);
    }
    
    const { data, error } = await query.order('nome');

    if (error) throw error;
    return data || [];
  }

  async criarSala(sala: Omit<Sala, 'id'>): Promise<Sala> {
    const { data, error } = await this.getSchema()
      .from('locais_salas')
      .insert(sala)
      .select('*')
      .single();

    if (error) throw error;
    return data as Sala;
  }

  async atualizarSala(id: string, sala: Partial<Omit<Sala, 'id'>>): Promise<void> {
    const { error } = await this.getSchema()
      .from('locais_salas')
      .update(sala)
      .eq('id', id);

    if (error) throw error;
  }

  async inativarSala(id: string): Promise<void> {
    const { error } = await this.getSchema()
      .from('locais_salas')
      .update({ ativo: false })
      .eq('id', id);

    if (error) throw error;
  }

  async ativarSala(id: string): Promise<void> {
    const { error } = await this.getSchema()
      .from('locais_salas')
      .update({ ativo: true })
      .eq('id', id);

    if (error) throw error;
  }

  async excluirSala(id: string): Promise<void> {
    const { error } = await this.getSchema()
      .from('locais_salas')
      .delete()
      .eq('id', id);

    if (error) throw error;
  }

  async inserirLocal(local: Omit<LocalMapeado, 'id'>): Promise<void> {
    const { error } = await this.getSchema()
      .from('locais')
      .insert(this.localToRow(local));

    if (error) throw error;
  }

  async atualizarLocal(id: string, local: Partial<LocalMapeado>): Promise<void> {
    const { error } = await this.getSchema()
      .from('locais')
      .update(this.localToRow(local))
      .eq('id', id);

    if (error) throw error;
  }

  async excluirLocal(id: string): Promise<void> {
    const { error } = await this.getSchema()
      .from('locais')
      .delete()
      .eq('id', id);

    if (error) throw error;
  }

  // Operações de Patrimônio
  async buscarBemPorTombo(tombo: string): Promise<BemPatrimonial | null> {
    const { data, error } = await this.getSchema()
      .from('view_patrimonio_unificado')
      .select('*')
      .eq('tombo', tombo)
      .limit(1);

    if (error) throw error;

    return data?.[0] || null;
  }

  // Operações de Inventário
  async registrarBemInventario(campusId: string, bem: BemPatrimonial): Promise<void> {
    const tabela = this.getTabelaInventario(campusId);
    const { error } = await this.getSchema()
      .from(tabela)
      .insert({
        campus: bem.campus ?? campusId,
        sede: bem.sede ?? null,
        bloco: bem.bloco ?? null,
        sala: bem.sala ?? null,
        tombo: bem.tombo,
        descricao: bem.descricao ?? null,
        danificado: bem.danificado ?? false,
        descricao_confere: bem.descricaoConfere ?? true,
        descricao_correta: bem.descricaoCorreta ?? null,
        observacao: bem.observacao ?? null,
        foto: bem.foto ?? null,
      } satisfies Record<string, any>);

    if (error) {
      const details = {
        message: (error as any).message,
        details: (error as any).details,
        hint: (error as any).hint,
        code: (error as any).code,
      };
      throw new Error(`insert ${this.schema}.${tabela} failed: ${JSON.stringify(details)}`);
    }
  }

  async buscarBensInventarioPorLocal(
    campusId: string,
    sede: string,
    bloco: string,
    andar: string,
    sala: string
  ): Promise<BemPatrimonial[]> {
    const tabela = this.getTabelaInventario(campusId);
    const { data, error } = await this.getSchema()
      .from(tabela)
      .select('*')
      .eq('sede', sede)
      .eq('bloco', bloco)
      .eq('andar', andar)
      .eq('sala', sala)
      .order('timestamp', { ascending: false });

    if (error) throw error;
    return data || [];
  }

  // Operações de Verificação
  async buscarDadosVerificacao(campusId: string, viewKey: string): Promise<any[]> {
    const tabela = this.getTabelaViewVerificacao(viewKey, campusId);
    const { data, error } = await this.getSchema()
      .from(tabela)
      .select('*');

    if (error) throw error;
    return data || [];
  }

  // Upload de arquivos (fotos)
  async uploadFoto(file: File, path: string): Promise<string> {
    const { data, error } = await this.client.storage
      .from('inventario-fotos')
      .upload(path, file, {
        cacheControl: '3600',
        upsert: false
      });

    if (error) throw error;

    const { data: urlData } = this.client.storage
      .from('inventario-fotos')
      .getPublicUrl(data.path);

    return urlData.publicUrl;
  }

  // Operações de Laudo
  async registrarSolicitacaoLaudo(laudo: SolicitacaoLaudoRegistro): Promise<SolicitacaoLaudoRegistro> {
    const { data, error } = await this.getSchema()
      .from('pedidosdelaudos')
      .insert(laudo)
      .select('*')
      .single();

    if (error) throw error;
    return data as SolicitacaoLaudoRegistro;
  }

  async atualizarStatusSolicitacaoLaudo(
    id: string,
    status: SolicitacaoLaudoRegistro['status'],
    webhookDisparadoEm?: string | null
  ): Promise<void> {
    const payload: Record<string, unknown> = { status };
    if (typeof webhookDisparadoEm !== 'undefined') {
      payload.webhook_disparado_em = webhookDisparadoEm;
    }

    const { error } = await this.getSchema()
      .from('pedidosdelaudos')
      .update(payload)
      .eq('id', id);

    if (error) throw error;
  }

  // Operações de Locais (SIORG)
  async atualizarLocalPorId(id: string, local: Partial<LocalMapeado>): Promise<void> {
    const { error } = await this.getSchema()
      .from('locais')
      .update(this.localToRow(local))
      .eq('id', id);

    if (error) throw error;
  }

  async excluirLocalPorId(id: string): Promise<void> {
    const { error } = await this.getSchema()
      .from('locais')
      .delete()
      .eq('id', id);

    if (error) throw error;
  }

  async listarUsuarios(): Promise<UsuarioAcesso[]> {
    const { data, error } = await this.getSchema()
      .from('usuario_acesso')
      .select('*')
      .order('papel')
      .order('email');

    if (error) throw error;
    return data || [];
  }

  async criarUsuario(params: {
    email: string;
    password: string;
    nome: string;
    papel: PapelUsuario;
    campus_siglas: string[];
    pode_ver_todos_campus: boolean;
  }): Promise<void> {
    const { data: sessionData } = await this.client.auth.getSession();
    const token = sessionData.session?.access_token;
    if (!token) throw new Error('Faça login para criar usuários.');

    const response = await fetch(`${ENV.SUPABASE_URL}/functions/v1/inventariar-create-user`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        apikey: ENV.SUPABASE_KEY,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(params),
    });

    if (!response.ok) {
      const text = await response.text();
      throw new Error(text || 'Erro ao criar usuário.');
    }
  }

  async atualizarUsuarioAcesso(id: string, payload: Partial<UsuarioAcesso>): Promise<void> {
    const { error } = await this.getSchema()
      .from('usuario_acesso')
      .update(payload)
      .eq('id', id);

    if (error) throw error;
  }

  async listarMovimentacoes(campusId: string, userId?: string): Promise<MovimentacaoPatrimonial[]> {
    let query = this.getSchema()
      .from('movimentacoes')
      .select('*')
      .eq('campus', campusId)
      .order('data_movimentacao', { ascending: false })
      .order('created_at', { ascending: false });

    if (userId) {
      query = query.eq('criado_por', userId);
    }

    const { data, error } = await query;

    if (error) throw error;
    return data || [];
  }

  async inserirMovimentacao(movimentacao: MovimentacaoPatrimonial): Promise<void> {
    const { error } = await this.getSchema()
      .from('movimentacoes')
      .insert(movimentacao);

    if (error) throw error;
  }

  async excluirMovimentacao(id: string): Promise<void> {
    const { error } = await this.getSchema()
      .from('movimentacoes')
      .delete()
      .eq('id', id);

    if (error) throw error;
  }


  private localToRow(local: Partial<LocalMapeado>) {
    return {
      campus: local.campus,
      sede: local.sede,
      bloco: local.bloco,
      descricao_bloco: local.descricaoBloco,
      andar: local.andar,
      sala: local.sala,
      setor: local.setor,
      descricao: local.descricao,
    };
  }
}

export const supabaseService = new SupabaseService();
export default supabaseService;
